import { writer } from "@/backend/write/BackendWriter";
import { boardTypeToLevel } from "@/model/baseTypes";
import type { BoardId } from "@/model/board";
import type { Objective, RAG } from "@/model/objective";
import { useBoardsStore } from "@/store/boards";
import { useObjectiveStore } from "@/store/objective";
import { useToastStore } from "@/store/toast";

import { ctxAction, defineActions } from "./actions";

function objectiveLevel(boardId: string) {
  return boardTypeToLevel(useBoardsStore().boardById(boardId).type);
}

export const objectiveActions = defineActions("objective", {
  add: ctxAction((ctx, e: Partial<Objective> & BoardId, isCommitted = true) => {
    const obj = useObjectiveStore().add(e, isCommitted);
    ctx.track({ objectiveId: obj.id, isStretch: !isCommitted, level: objectiveLevel(e.boardId) });
    writer.addObjective(e.boardId, obj, !isCommitted);
    return obj;
  }),
  remove: ctxAction((ctx, boardId: string, objectiveId: string) => {
    ctx.track({ objectiveId, level: objectiveLevel(boardId) });
    useObjectiveStore().remove({ id: objectiveId, boardId });
    writer.deleteObjective(boardId, objectiveId);
    useToastStore().show(/*$t*/ "objective.deleted", { invisible: true });
  }),
  move: ctxAction(
    (ctx, boardId: string, objectiveId: string, data: { stretch: boolean; rank: number }) => {
      ctx.track({ objectiveId, level: objectiveLevel(boardId) });
      useObjectiveStore().move({ id: objectiveId, boardId, ...data });
      writer.moveObjective(boardId, objectiveId, data.stretch, data.rank);
    },
  ),
  setTitle: ctxAction(
    (ctx, boardId: string, objective: Objective, title: string) => {
      ctx.track({ objectiveId: objective.id, level: objectiveLevel(boardId) });
      objective.text = title;
      useObjectiveStore().update({ boardId, ...objective });
      writer.updateObjective(boardId, objective);
    },
    { history: { merge: true, saveState: (_, obj) => ({ id: obj.id }) } },
  ),
  setDescription: ctxAction(
    (ctx, boardId: string, objective: Objective, description: string) => {
      ctx.track({ objectiveId: objective.id, level: objectiveLevel(boardId) });
      objective.description = description;
      useObjectiveStore().update({ boardId, ...objective });
      writer.updateObjective(boardId, objective);
    },
    { history: { merge: true, saveState: (_, obj) => ({ id: obj.id }) } },
  ),
  setAv: ctxAction(
    (ctx, boardId: string, objective: Objective, isCommitted: boolean, av: number) => {
      ctx.track({
        hadPreviousValue: (objective.av ?? 0) !== 0,
        hasNewValue: true,
        objectiveId: objective.id,
        isStretch: !isCommitted,
        level: objectiveLevel(boardId),
      });
      objective.av = av;
      useObjectiveStore().update({ boardId, ...objective });
      writer.updateObjective(boardId, objective);
    },
  ),
  setBv: ctxAction(
    (ctx, boardId: string, objective: Objective, isCommitted: boolean, bv: number) => {
      ctx.track({
        hadPreviousValue: (objective.bv ?? 0) !== 0,
        hasNewValue: true,
        objectiveId: objective.id,
        isStretch: !isCommitted,
        level: objectiveLevel(boardId),
      });
      objective.bv = bv;
      useObjectiveStore().update({ boardId, ...objective });
      writer.updateObjective(boardId, objective);
    },
  ),
  setRag: ctxAction((ctx, boardId: string, objective: Objective, rag: RAG) => {
    ctx.track({ objectiveId: objective.id, level: objectiveLevel(boardId) });
    objective.rag = rag;
    useObjectiveStore().update({ boardId, ...objective });
    writer.updateObjective(boardId, objective);
  }),
});
